/**
 * Underfanene i Premier og Plan (#153, PLAN.md steg 3 punkt 4).
 *
 * Premier: Belønninger · Butikk · Merker · Tavle
 * Plan:    Kalender · Middag · Handle
 *
 * Hver fane er en egen rute som før, så layouten monteres av rutene i gruppen
 * (App.tsx) og legger `SegmentedTabs` i lenkemodus over skjermen. Ingen URL
 * flyttes. Lommebok (/poeng) hører til Premier i hovedmenyen, men er ikke en
 * underfane — skissen når den fra et kort.
 *
 * En fane merket `ownTabs` tegner fanene selv med `SubTabs`, der skissen vil ha
 * dem: på Belønninger under toppen og lommebokkortet, på Butikk, Merker og
 * Tavle rett under toppen med saldopillen (#156, `PremierFrame`), og på Kalender
 * og Middag i datopanelet (#157). Layouten legger
 * dem da ikke over skjermen. Merket er per fane og uavhengig av om skjermen har
 * egen topp, så en skjerm som får sin egen ScreenHeader beholder fanene til
 * den selv tar dem i bruk.
 */
import type { ReactNode } from 'react';
import { matchPath, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SegmentedTabs } from '../../components/primitives/SegmentedTabs';

interface SubTab {
  to: string;
  i18nKey: string;
  testId: string;
  wide?: boolean;
  /** Skjermen tegner fanene selv med `SubTabs`. */
  ownTabs?: boolean;
}

export type SubTabGroup = 'rewards' | 'plan';

const GROUPS: Record<SubTabGroup, { label: string; links: readonly SubTab[] }> = {
  rewards: {
    label: 'nav.rewards',
    links: [
      // Skissen gir «Belønninger» halvannen andel; ellers står ordet kant i kant.
      { to: '/belonninger', i18nKey: 'nav.rewardsTabs.rewards', testId: 'subtab-rewards', wide: true, ownTabs: true },
      { to: '/butikk', i18nKey: 'nav.rewardsTabs.shop', testId: 'subtab-shop', ownTabs: true },
      { to: '/utmerkelser', i18nKey: 'nav.rewardsTabs.badges', testId: 'subtab-badges', ownTabs: true },
      { to: '/leaderboard', i18nKey: 'nav.rewardsTabs.board', testId: 'subtab-board', ownTabs: true },
    ],
  },
  plan: {
    label: 'nav.plan',
    links: [
      // nav-tab-calendar og nav-tab-shop var hovedmenyfaner før #153; nå er
      // underfanene de tilsvarende elementene.
      // Kalender og Middag legger fanene i datopanelet (#157, steg 7a og 7c).
      { to: '/kalender', i18nKey: 'nav.calendar', testId: 'nav-tab-calendar', ownTabs: true },
      { to: '/meny', i18nKey: 'nav.meal', testId: 'subtab-meal', ownTabs: true },
      { to: '/handleliste', i18nKey: 'nav.shop', testId: 'nav-tab-shop', ownTabs: true },
    ],
  },
};

/**
 * Underfanene i en gruppe, for skjermer som plasserer dem selv. `tone="panel"`
 * når de ligger på datopanelet (Kalender og Middag, #157).
 */
export function SubTabs({
  group,
  tone,
  className,
}: {
  group: SubTabGroup;
  tone?: 'default' | 'panel';
  className?: string;
}): ReactNode {
  const { t } = useTranslation();
  const { label, links } = GROUPS[group];
  return (
    <SegmentedTabs
      variant="links"
      label={t(label)}
      data-testid={`subtabs-${group}`}
      {...(tone ? { tone } : {})}
      className={['md:max-w-xl', className].filter(Boolean).join(' ')}
      links={links.map(({ to, testId, wide, i18nKey }) => ({
        to,
        testId,
        label: t(i18nKey),
        ...(wide ? { wide } : {}),
      }))}
    />
  );
}

export function SubTabsLayout({ group }: { group: SubTabGroup }): ReactNode {
  const { pathname } = useLocation();
  const ownTabs = GROUPS[group].links.some(
    (l) => l.ownTabs && matchPath({ path: l.to, end: true }, pathname) !== null,
  );
  return (
    <>
      {ownTabs ? null : (
        <div className="px-5 pt-3 md:px-8">
          <SubTabs group={group} />
        </div>
      )}
      <Outlet />
    </>
  );
}
